import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import {
  Truck,
  MapPin,
  Calendar,
  ClipboardCopy,
  ArrowLeft,
  Clock,
  Plane,
  Warehouse,
  Home,
  HelpCircle,
  ListOrdered,
} from "lucide-react"
import { Link, useParams } from "react-router-dom"
import { useState, useEffect } from "react"
import { getUserOrders, getOrderById } from "@/utils/helper/order"
import Loader from "@/components/loader/loader"
import { toast } from "sonner"

export default function TrackShipmentPage() {
  const { trackingId } = useParams()
  const [shipment, setShipment] = useState(null)
  const [orderId, setOrderId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Find order by tracking number
  useEffect(() => {
    const findOrderByTracking = async () => {
      if (!trackingId) return
      
      try {
        setLoading(true)
        setError(null)
        
        // Get all orders and find the one with matching tracking number
        const orders = await getUserOrders()
        let foundOrder = null
        
        for (const order of orders) {
          // Check if order has shipments with matching tracking number
          if (order.shipments && Array.isArray(order.shipments)) {
            const matchingShipment = order.shipments.find(
              s => s.tracking_number === trackingId
            )
            if (matchingShipment) {
              foundOrder = order
              break
            }
          }
        }
        
        if (!foundOrder) {
          // Try to get order details directly if we have order_id in the tracking number format
          // Or search through orders more thoroughly
          setError("Tracking information not found")
          setLoading(false)
          return
        }
        
        // Get full order details
        const orderData = await getOrderById(foundOrder.order_id)
        const matchingShipment = orderData.shipments?.find(
          s => s.tracking_number === trackingId
        )
        
        if (!matchingShipment) {
          setError("Tracking information not found")
          setLoading(false)
          return
        }
        
        setOrderId(foundOrder.order_id)
        
        // Build events from order history
        const events = []
        if (orderData.history && Array.isArray(orderData.history)) {
          orderData.history.forEach((historyItem, index) => {
            let icon = ClipboardCopy
            let description = historyItem.status || "Status update"
            
            if (historyItem.status === "Shipped") {
              icon = Truck
              description = "Package shipped"
            } else if (historyItem.status === "Delivered") {
              icon = Home
              description = "Package delivered"
            } else if (historyItem.status === "Processing") {
              icon = Warehouse
              description = "Order processing"
            }
            
            events.push({
              timestamp: historyItem.changed_at || historyItem.timestamp,
              location: "Location not available",
              description: description,
              icon: icon,
              note: historyItem.note,
            })
          })
        }
        
        // Add shipment-specific events if available
        if (matchingShipment.shipped_at) {
          events.unshift({
            timestamp: matchingShipment.shipped_at,
            location: "Origin",
            description: "Package shipped",
            icon: Truck,
          })
        }
        
        if (matchingShipment.delivered_at) {
          events.unshift({
            timestamp: matchingShipment.delivered_at,
            location: "Destination",
            description: "Package delivered",
            icon: Home,
          })
        }
        
        // Sort events by timestamp (newest first)
        events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        
        setShipment({
          trackingNumber: trackingId,
          carrier: matchingShipment.courier || "Standard Carrier",
          status: orderData.status || "In Transit",
          estimatedDelivery: matchingShipment.estimated_delivery
            ? new Date(matchingShipment.estimated_delivery).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })
            : null,
          origin: "Origin location",
          destination: "Destination location",
          orderId: foundOrder.order_id,
          events: events.length > 0 ? events : [
            {
              timestamp: new Date().toISOString(),
              location: "Tracking information",
              description: "Shipment information received",
              icon: ClipboardCopy,
            },
          ],
        })
      } catch (err) {
        console.error("Error fetching tracking information:", err)
        setError(err.message || "Failed to load tracking information")
        toast.error(err.message || "Failed to load tracking information")
      } finally {
        setLoading(false)
      }
    }
    
    findOrderByTracking()
  }, [trackingId])

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Delivered":
        return "bg-green-100 text-green-700"
      case "In Transit":
        return "bg-blue-100 text-blue-700"
      case "Processing":
        return "bg-yellow-100 text-yellow-700"
      case "Cancelled":
        return "bg-red-100 text-red-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  const formatDateTime = (isoString) => {
    const date = new Date(isoString)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  }

  if (loading) {
    return <Loader />
  }

  if (error || !shipment) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <Truck className="w-16 h-16 mb-4" />
        <p className="text-lg">{error || "Tracking information not found."}</p>
        <Link to="/dashboard/orders">
          <Button variant="link" className="mt-4">
            Back to Orders
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Truck className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-primary">Track Shipment</h1>
        </div>
        {orderId && (
          <Link to={`/dashboard/orders/${orderId}`}>
            <Button
              variant="outline"
              className="border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Order
            </Button>
          </Link>
        )}
      </div>
      <p className="text-gray-600">Real-time updates for your shipment {shipment.trackingNumber}.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Shipment Summary */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Shipment Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-secondary" />
                <div>
                  <p className="font-medium text-primary">Tracking Number:</p>
                  <p className="text-lg font-bold text-primary">{shipment.trackingNumber}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={getStatusBadgeColor(shipment.status)}>{shipment.status}</Badge>
                <p className="text-sm text-gray-600">Carrier: {shipment.carrier}</p>
              </div>
              {shipment.estimatedDelivery && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-secondary" />
                  <div>
                    <p className="font-medium text-primary">Estimated Delivery:</p>
                    <p className="text-lg font-bold text-primary">{shipment.estimatedDelivery}</p>
                  </div>
                </div>
              )}
              <Separator />
              <div className="space-y-2 text-gray-700">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-secondary" />
                  <span>Origin: {shipment.origin}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-secondary" />
                  <span>Destination: {shipment.destination}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Need Help?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full bg-primary hover:bg-primary/90 text-white">
                <HelpCircle className="w-4 h-4 mr-2" /> Contact Carrier
              </Button>
              {orderId && (
                <Link to={`/dashboard/orders/${orderId}`}>
                  <Button
                    variant="outline"
                    className="w-full border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
                  >
                    <ListOrdered className="w-4 h-4 mr-2" /> View Order Details
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tracking Timeline */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Tracking History</CardTitle>
              <CardDescription>All events related to your shipment.</CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="relative border-l border-gray-200 ml-4">
                {shipment.events.map((event, index) => (
                  <li key={index} className="mb-8 ml-6">
                    <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-secondary ring-8 ring-white">
                      {event.icon && <event.icon className="h-4 w-4 text-primary" />}
                    </span>
                    <h3 className="mb-1 flex items-center text-lg font-semibold text-primary">
                      {event.description}
                      {index === 0 && <Badge className="ml-3 bg-green-100 text-green-700">Latest</Badge>}
                    </h3>
                    <time className="block mb-2 text-sm font-normal leading-none text-gray-500">
                      <Clock className="inline-block w-4 h-4 mr-1 align-text-bottom" />
                      {formatDateTime(event.timestamp)}
                    </time>
                    <p className="text-base font-normal text-gray-700">{event.location}</p>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
